import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  DateFilterConstantsEnum,
  FilterTypes,
} from '../../../../../interfaces/src/lib/enums/index';
import { getDealsFilters, getDealsPromotions } from '../../../index';
import { getQueryParamIgnoreCase } from '../../utilFuncs/utilFuncs';
import dayjs from 'dayjs';

/**
 * Type representing an array of selected dates and null values.
 * The first element represents the start date and the second represents the end date.
 */
type SelectedDates = {
  dates: (string | null)[];
  isFirstDateValid: boolean;
  isSecondDateValid: boolean;
  isFromUrl?: boolean;
};

/**
 * Type representing an array of selected periods.
 */
type SelectedPeriods = string[];

/**
 * Type representing the last active tab in the date filter component.
 */
type LastActiveTab = string | null;

type SelectionSource = string | null;

/**
 * Interface representing the state of the date filter slice.
 */
export interface DateFilterState {
  // Array of selected dates and null values
  selectedDates: SelectedDates;
  // Array of selected periods
  selectedPeriods: SelectedPeriods;
  // Last active tab in the date filter component
  lastActiveTab: LastActiveTab;
  // The source of the selected date
  selectionSource: SelectionSource;
  // The last state of Daily Calendar
  dailyCalendarViewDate: string;
}

/**
 * Interface representing the query parameters for the date filter.
 */
interface dateQueryParams {
  // Departure date from query parameter
  departureDateFrom?: string | null;
  // Departure date to query parameter
  departureDateTo?: string | null;
  // Periods query parameter
  periods?: string | null;
}

/**
 * Parses the query parameters from the current URL and returns an object with the selected dates and periods.
 * @returns Object with the selected dates and periods.
 */
function parseQueryInit(): { selectedDates: SelectedDates; selectedPeriods: SelectedPeriods } {
  const urlParams = window.location.search;
  const queryParams: dateQueryParams = {
    departureDateFrom: getQueryParamIgnoreCase(urlParams, 'departureDateFrom'),
    departureDateTo: getQueryParamIgnoreCase(urlParams, 'departureDateTo'),
    periods: getQueryParamIgnoreCase(urlParams, 'periods'),
  };

  /**
   * Parses the value of a query parameter and returns an array of selected periods.
   * @param paramValue - The value of the query parameter.
   * @returns Array of selected periods.
   */
  const parseValues = (paramValue: string | null | undefined): SelectedPeriods => {
    if (!paramValue) return [];
    return paramValue.split(',').map((item) => decodeURIComponent(item).replace(/\+/g, ' ').trim());
  };

  // Get the current date at the start of the day
  const today = dayjs().startOf('day');

  // Get the selected dates from the query parameters and convert them to the correct format
  const departureDateFrom = queryParams.departureDateFrom
    ? dayjs(queryParams?.departureDateFrom?.replace(/-/g, '/'))
    : undefined;
  const departureDateTo = queryParams.departureDateTo
    ? dayjs(queryParams?.departureDateTo?.replace(/-/g, '/'))
    : undefined;

  // Initial selectedDates state
  const selectedDates: SelectedDates = {
    dates: [null, null],
    isFirstDateValid: true,
    isSecondDateValid: true,
    isFromUrl: true,
  };

  // Update the value and the flag when retrieving the selected dates for departureDateFrom and departureDateTo
  if (departureDateFrom && (!departureDateFrom.isValid() || departureDateFrom.isBefore(today))) {
    selectedDates.dates[0] = null;
    selectedDates.isFirstDateValid = false;
  } else if (departureDateFrom) {
    selectedDates.dates[0] = departureDateFrom.format(DateFilterConstantsEnum.FORMAT);
  }

  if (departureDateTo && (!departureDateTo.isValid() || departureDateTo.isBefore(today))) {
    selectedDates.dates[1] = null;
    selectedDates.isSecondDateValid = false;
  } else if (departureDateTo) {
    selectedDates.dates[1] = departureDateTo.format(DateFilterConstantsEnum.FORMAT);
  }

  if (departureDateTo && departureDateFrom && departureDateTo.isBefore(departureDateFrom)) {
    selectedDates.dates[1] = null;
    selectedDates.isSecondDateValid = false;
  }

  if (departureDateFrom && !departureDateTo) {
    selectedDates.dates[1] = null;
    selectedDates.isSecondDateValid = false;
  }

  if (departureDateTo && !departureDateFrom) {
    selectedDates.dates[0] = null;
    selectedDates.isFirstDateValid = false;
  }

  // Get the selected periods from the query parameters
  const selectedPeriods: SelectedPeriods = parseValues(queryParams?.periods ?? null);
  return { selectedDates, selectedPeriods };
}

// Initialize the state of the date filter slice
const initialState: DateFilterState & {
  pastState: {
    selectedDates?: SelectedDates;
    selectedPeriods?: SelectedPeriods;
  };
} = {
  selectedDates: parseQueryInit().selectedDates,
  selectedPeriods: parseQueryInit().selectedPeriods,
  dailyCalendarViewDate: '',
  lastActiveTab: null,
  selectionSource: null,
  pastState: {},
};

export const resetDealsDateFilter = createAsyncThunk(
  'dateFilter/resetDealsDateFilter',
  async (_, { dispatch }) => {
    let dealsStartDate: string | null = null;
    let dealsEndDate: string | null = null;

    // Fetch the deals filters
    const dealsFilters = getDealsFilters();

    // Extract start and end dates from deals filters
    dealsFilters.forEach((filter) => {
      if (filter.filterType === FilterTypes.START_DATE) {
        dealsStartDate = filter.filterValue;
      }
      if (filter.filterType === FilterTypes.END_DATE) {
        dealsEndDate = filter.filterValue;
      }
    });

    // Fallback to deals promotions if necessary
    dealsStartDate = getDealsPromotions()?.startDate || dealsStartDate;
    dealsEndDate = getDealsPromotions()?.endDate || dealsEndDate;

    // Prepare the data for updating state
    const formattedStartDate = dealsStartDate
      ? dayjs(dealsStartDate).format(DateFilterConstantsEnum.FORMAT)
      : null;
    const formattedEndDate = dealsEndDate
      ? dayjs(dealsEndDate).format(DateFilterConstantsEnum.FORMAT)
      : null;

    // Dispatch an action to reset the selected dates
    dispatch(
      setSelectedDates({
        dates: [formattedStartDate, formattedEndDate],
        isFirstDateValid: true,
        isSecondDateValid: true,
        isFromUrl: false,
      })
    );
    dispatch(setLastActiveTab(null));
    dispatch(setSelectedPeriods([]));
    dispatch(resetDatesPastState());
    dispatch(setSelectionSource(null));
  }
);

/**
 * Slice representing the date filter state.
 */
const dateFilterSlice = createSlice({
  name: 'dateFilter',
  initialState,
  reducers: {
    /**
     * Reducer to set the selected dates.
     * @param state - The current state of the date filter slice.
     * @param action - The action containing the new selected dates.
     */
    setSelectedDates: (state, action: PayloadAction<SelectedDates>) => {
      state.selectedDates = action.payload;
    },
    setDailyCalendarViewDate: (state, action: PayloadAction<string>) => {
      state.dailyCalendarViewDate = action.payload;
    },
    /**
     * Reducer to set the selected periods.
     * @param state - The current state of the date filter slice.
     * @param action - The action containing the new selected periods.
     */
    setSelectedPeriods: (state, action: PayloadAction<SelectedPeriods>) => {
      state.selectedPeriods = action.payload;
    },
    /**
     * Reducer to set the last active tab.
     * @param state - The current state of the date filter slice.
     * @param action - The action containing the new last active tab.
     */
    setLastActiveTab: (state, action: PayloadAction<LastActiveTab>) => {
      state.lastActiveTab = action.payload;
    },
    setSelectionSource: (state, action: PayloadAction<SelectionSource>) => {
      state.selectionSource = action.payload;
    },
    setDatesPastState: (state, action: PayloadAction<Partial<DateFilterState>>) => {
      if (Object.keys(state.pastState).length === 0) state.pastState = action.payload;

      return state;
    },
    resetDatesPastState: (state) => {
      state.pastState = {};

      return state;
    },
    restoreDatesToPastState: (state) => {
      if (state?.pastState?.selectedDates) state.selectedDates = state.pastState.selectedDates;
      if (state?.pastState?.selectedPeriods)
        state.selectedPeriods = state.pastState.selectedPeriods;

      state.pastState = {};

      return state;
    },
    /**
     * Reducer to reset the date filter.
     * @param state - The current state of the date filter slice.
     */
    resetDateFilter: (state) => {
      state.selectedDates = {
        dates: [null, null],
        isFirstDateValid: true,
        isSecondDateValid: true,
        isFromUrl: false,
      };
      state.selectedPeriods = [];
      state.lastActiveTab = null;
      state.selectionSource = null;
      state.pastState = {};
    },
    /**
     * Reducer to reset the selected dates to the initial deals values.
     * @param state - The current state of the date filter slice.
     * @param action - The action containing the dealsStartDate and dealsEndDate.
     */
  },
});

// Export the actions and reducer of the date filter slice
export const {
  setSelectedDates,
  setSelectedPeriods,
  setLastActiveTab,
  setSelectionSource,
  resetDateFilter,
  setDatesPastState,
  resetDatesPastState,
  restoreDatesToPastState,
  setDailyCalendarViewDate,
} = dateFilterSlice.actions;
export default dateFilterSlice.reducer;
